import React, { useState } from "react";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const PRESETS = [
  { key: "3m", label: "3 mo", months: 3 },
  { key: "6m", label: "6 mo", months: 6 },
  { key: "12m", label: "12 mo", months: 12 },
  { key: "24m", label: "24 mo", months: 24 },
];

function fmt(d) {
  return d
    ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "";
}

/**
 * DateRangePicker — preset buttons + a calendar popover for custom ranges.
 *
 * Controlled value shape:
 *   { months?: number, from?: Date, to?: Date }
 * Only one of the two flavors is set at a time.
 */
export default function DateRangePicker({ value, onChange, testId = "date-range" }) {
  const [open, setOpen] = useState(false);
  const [tempRange, setTempRange] = useState(
    value?.from && value?.to ? { from: value.from, to: value.to } : undefined
  );

  const activeMonths = value?.months;
  const hasCustom = !!(value?.from && value?.to);

  const pickPreset = (p) => {
    onChange({ months: p.months });
    setTempRange(undefined);
  };

  const applyCustom = () => {
    if (tempRange?.from && tempRange?.to) {
      onChange({ from: tempRange.from, to: tempRange.to });
      setOpen(false);
    }
  };

  const clearCustom = () => {
    onChange({ months: 12 });
    setTempRange(undefined);
  };

  return (
    <div className="flex items-center gap-1" data-testid={testId}>
      <div className="hidden items-center gap-0.5 rounded-sm border border-slate-200 bg-white p-0.5 md:flex">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => pickPreset(p)}
            data-testid={`${testId}-preset-${p.key}`}
            className={[
              "rounded-sm px-3 py-1.5 text-xs font-medium transition-colors",
              activeMonths === p.months && !hasCustom
                ? "bg-navy-600 text-white"
                : "text-slate-500 hover:text-navy-600",
            ].join(" ")}
          >
            {p.label}
          </button>
        ))}
      </div>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`${testId}-custom-trigger`}
            className={[
              "flex h-9 items-center gap-1.5 rounded-sm border px-3 text-xs transition-colors",
              hasCustom
                ? "border-brand-orange bg-brand-orange/10 text-brand-orange"
                : "border-slate-200 text-slate-600 hover:border-navy-600 hover:text-navy-600",
            ].join(" ")}
          >
            <CalendarIcon className="h-3.5 w-3.5" />
            {hasCustom ? `${fmt(value.from)} → ${fmt(value.to)}` : "Custom range"}
            {hasCustom && (
              <span
                role="button"
                onClick={(e) => {
                  e.stopPropagation();
                  clearCustom();
                }}
                className="ml-1 rounded-sm p-0.5 hover:bg-brand-orange/20"
              >
                <X className="h-3 w-3" />
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto p-0" data-testid={`${testId}-popover`}>
          <Calendar
            mode="range"
            selected={tempRange}
            onSelect={setTempRange}
            numberOfMonths={2}
            defaultMonth={tempRange?.from || value?.from || new Date()}
          />
          <div className="flex items-center justify-between border-t border-slate-100 p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              {tempRange?.from && tempRange?.to
                ? `${fmt(tempRange.from)} → ${fmt(tempRange.to)}`
                : "Pick start and end"}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="h-8 rounded-sm border border-slate-200 px-3 text-xs text-slate-600 hover:border-navy-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyCustom}
                disabled={!tempRange?.from || !tempRange?.to}
                data-testid={`${testId}-apply`}
                className="h-8 rounded-sm bg-brand-orange px-3 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#C1701C] disabled:opacity-50"
              >
                Apply
              </button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/** Convert a range value into query params for the backend. */
export function rangeToQuery(value) {
  if (value?.from && value?.to) {
    return {
      start_date: value.from.toISOString(),
      end_date: value.to.toISOString(),
    };
  }
  return { months: value?.months ?? 12 };
}

export function rangeToQueryString(value) {
  const q = rangeToQuery(value);
  return new URLSearchParams(
    Object.fromEntries(Object.entries(q).filter(([, v]) => v !== undefined))
  ).toString();
}
